import { Field, FieldValues, isOptionObject } from '../schema';

const deriveGpsTrackingMode = (config: FieldValues): string => {
  if (config.gps_enabled) return 'gps_enabled';
  if (Number(config.periodic_gps_period) > 0 && !config.gps_enabled) {
    return 'periodic_gps';
  }
  return 'gps_only_alerts';
};

const deriveWatcherrSounds = (config: FieldValues): string => {
  if (!config.watch_muted && config.panic_button_muted) {
    return 'mute_alert_sounds';
  }
  if (config.watch_muted) return 'mute_all_sounds';
  return 'all_sounds';
};

export const mapConfigToForm = (config: FieldValues): FieldValues => {
  if (!config) return {};
  return {
    ...config,
    gps_tracking_mode: deriveGpsTrackingMode(config),
    watcherr_sounds: deriveWatcherrSounds(config),
  };
};

// The config key(s) a form field maps to — itself, unless it is synthetic, in which case the
// schema names them. A field counts as differing when any of its source keys differs.
export const configKeysForField = (field: Field): string[] => field.source_keys ?? [field.id];

/**
 * A field whose id is not a configuration parameter. Three kinds:
 *
 * - a synthetic field, which stands in for the parameters its options write;
 * - a preset selector, whose options write theirs when picked;
 * - any other control owning several parameters, which `source_keys` names and whose own id is not
 *   among them. The zone picker is the case -- it owns a resident's zone list and the preset that
 *   can stand in for it, and `indoor-geofencing-zone-selection` is not a setting.
 *
 * The last is the general rule and would cover the first two; they are kept named because a
 * synthetic field is also expanded below and a preset is not.
 */
const namesNoParameter = (field: Field): boolean =>
  Boolean(field.synthetic) ||
  (field.options ?? []).some((o) => isOptionObject(o) && o.props) ||
  Boolean(field.source_keys && !field.source_keys.includes(field.id));

// A synthetic field's id names no config parameter, so it never reaches the device. Selecting an
// option already writes that option's `props` into the form (see `applySubProfile`); re-applying
// them here settles the case where the form was seeded from a device whose values do not quite
// match the mode they derive to.
export const mapFormToConfig = (form: FieldValues, fields: Field[]): FieldValues => {
  const synthetic = fields.filter((f) => f.synthetic);
  const notAParameter = new Set(fields.filter(namesNoParameter).map((f) => f.id));

  const out: FieldValues = {};
  for (const [key, value] of Object.entries(form)) {
    if (!notAParameter.has(key)) out[key] = value;
  }

  for (const field of synthetic) {
    const selected = (field.options ?? []).find((o) => isOptionObject(o) && o.value === form[field.id]);
    if (selected && isOptionObject(selected) && selected.props) Object.assign(out, selected.props);
  }
  return out;
};
